import {
  Badge,
  Button,
  EmptyState,
  KeyValue,
  List,
  ListRow,
  PageHeader,
  Section,
  Skeleton,
  Text,
} from '@ui';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { useSession } from '../../identity/session';
import { useMarket, type Market } from '../api';
import { AssortmentDialog } from '../components/AssortmentDialog';
import { MarketDialog } from '../components/MarketDialog';
import { useReferrerName } from '../components/Referrer';
import { SellersDialog } from '../components/SellersDialog';
import { scheduleText, timesText } from '../components/scheduleText';
import { formatEuro } from '../format';
import styles from './SalesPages.module.css';

/** A market with all its days and the totals over the season (MKT-003). */
export function MarketPage() {
  const { t } = useTranslation();
  const { marketId = '' } = useParams();
  const market = useMarket(marketId);
  if (market.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(market.error)}
        actions={<BackLink to="/verkauf/maerkte" label={t('sales.tabs.markets')} />}
      />
    );
  }
  if (!market.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={8} />
      </div>
    );
  }
  return <MarketDetail market={market.data} />;
}

function MarketDetail({ market }: { market: Market }) {
  const { t } = useTranslation();
  const { can } = useSession();
  const [editing, setEditing] = useState(false);
  const [assortment, setAssortment] = useState(false);
  const [sellers, setSellers] = useState(false);
  const sellerName = useReferrerName();
  const write = can('sales.write');
  const times = timesText(t, market.starts_at, market.ends_at);
  const meta = [
    scheduleText(t, market.schedule),
    ...(times ? [times] : []),
    ...(market.place ? [market.place] : []),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        back={<BackLink to="/verkauf/maerkte" label={t('sales.tabs.markets')} />}
        title={market.name}
        meta={meta.map((m, i) => (
          <Fragment key={i}>
            {i > 0 && ' · '}
            {m}
          </Fragment>
        ))}
        actions={
          write && (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              {t('sales.markets.edit')}
            </Button>
          )
        }
      />
      <div className={styles.columns}>
        <Section title={t('sales.markets.daysTitle')} variant="flush">
          {market.days.length === 0 ? (
            <EmptyState title={t('sales.markets.noDays')} />
          ) : (
            <List>
              {market.days.map((d) => (
                <ListRow
                  key={d.id}
                  href={`/verkauf/markttage/${d.id}`}
                  title={formatDate(d.held_on)}
                  description={timesText(t, d.starts_at, d.ends_at) ?? undefined}
                  trailing={
                    d.status === 'canceled' ? (
                      <Badge tone="neutral" size="sm">
                        {t('sales.market.status.canceled')}
                      </Badge>
                    ) : d.status === 'open' ? (
                      <Badge tone="success" size="sm">
                        {t('sales.market.status.open')}
                      </Badge>
                    ) : (
                      <Text as="span" numeric>
                        {formatEuro(d.total_cents)}
                      </Text>
                    )
                  }
                />
              ))}
            </List>
          )}
        </Section>
        <div className={styles.stack}>
          <Section title={t('sales.markets.totalTitle')}>
            <KeyValue
              items={[
                {
                  key: 'total',
                  label: t('sales.market.total'),
                  value: (
                    <Text as="span" numeric>
                      {formatEuro(market.total_cents)}
                    </Text>
                  ),
                },
                { key: 'sales', label: t('sales.market.sales'), value: String(market.sales) },
                {
                  key: 'days',
                  label: t('sales.markets.daysTitle'),
                  value: String(market.days.filter((d) => d.status !== 'canceled').length),
                },
                {
                  key: 'notes',
                  label: t('sales.markets.notes'),
                  value: market.notes && <span className={styles.notes}>{market.notes}</span>,
                },
              ]}
            />
          </Section>
          {(market.by_seller.length > 0 || (write && true)) && (
            <Section
              title={t('sales.sellers.title')}
              action={
                write &&
                true && (
                  <Button variant="secondary" size="sm" onClick={() => setSellers(true)}>
                    {t('sales.sellers.edit')}
                  </Button>
                )
              }
              footer={t('sales.sellers.marketHint')}
            >
              {market.by_seller.length === 0 ? (
                <List>
                  <ListRow title={t('sales.sellers.none')} />
                </List>
              ) : (
                <List>
                  {market.by_seller.map((s) => (
                    <ListRow
                      key={s.seller.id}
                      title={sellerName(s.seller)}
                      description={t('sales.market.salesCount', { count: s.sales })}
                      trailing={
                        <Text as="span" numeric>
                          {formatEuro(s.total_cents)}
                        </Text>
                      }
                    />
                  ))}
                </List>
              )}
            </Section>
          )}
          <Section
            title={t('sales.assortment.title')}
            action={
              write && (
                <Button variant="secondary" size="sm" onClick={() => setAssortment(true)}>
                  {t('sales.assortment.edit')}
                </Button>
              )
            }
            footer={t('sales.markets.assortmentHint')}
          >
            {market.assortment.length === 0 ? (
              <List>
                <ListRow title={t('sales.assortment.none')} />
              </List>
            ) : (
              <List>
                {market.assortment.map((a) => (
                  <ListRow
                    key={a.article.id}
                    title={a.article.name}
                    trailing={
                      <Text as="span" numeric>
                        {formatEuro(a.price_cents ?? a.list_price_cents ?? 0)}
                      </Text>
                    }
                  />
                ))}
              </List>
            )}
          </Section>
        </div>
      </div>
      {assortment && <AssortmentDialog market={market} onClose={() => setAssortment(false)} />}
      {sellers && <SellersDialog market={market} onClose={() => setSellers(false)} />}
      {editing && <MarketDialog market={market} onClose={() => setEditing(false)} />}
    </div>
  );
}
